import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Bank account state for institutional investor
 * Stores banking information for fund transfers
 */
export interface InstitutionalBankState {
  bankName: string | null;
  bankCode: string | null;
  accountNumber: string | null;
  accountType: string | null;
  accountHolderName: string | null;
  swiftCode: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

const initialState: InstitutionalBankState = {
  bankName: null,
  bankCode: null,
  accountNumber: null,
  accountType: null,
  accountHolderName: null,
  swiftCode: null,
  bankAddress: null,
  bankCountry: null,
  currency: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof InstitutionalBankState>(field: K) => {
  return (state: InstitutionalBankState, action: PayloadAction<InstitutionalBankState[K]>) => {
    state[field] = action.payload;
  };
};

const bankSlice = createSlice({
  name: "bank",
  initialState,
  reducers: {
    // Bank account field setters
    setBankNameRedux: createFieldSetter("bankName"),
    setBankCodeRedux: createFieldSetter("bankCode"),
    setAccountNoRedux: createFieldSetter("accountNumber"),
    setAccountTypeRedux: createFieldSetter("accountType"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),
    setSwiftCodeReduct: createFieldSetter("swiftCode"),
    setBankAddressRedux: createFieldSetter("bankAddress"),
    setBankCountryRedux: createFieldSetter("bankCountry"),
    setCurrencyRedux: createFieldSetter("currency"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateInstitutionalBankState: (
      state,
      action: PayloadAction<Partial<InstitutionalBankState>>,
    ) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetInstitutionalBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setBankCodeRedux,
  setAccountNoRedux,
  setAccountTypeRedux,
  setAccountHolderNameRedux,
  setSwiftCodeReduct,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
  hydrateInstitutionalBankState,
  resetInstitutionalBankState,
} = bankSlice.actions;

export default bankSlice.reducer;
